import { db, auth } from '../firebaseConfig';
import { doc, setDoc, serverTimestamp, increment, collection, addDoc } from 'firebase/firestore';
import { trackGA4Event, getGA4Context } from './GA4Analytics';

// Helper to get or generate session ID
const getSessionId = () => {
    if (typeof window === 'undefined') return 'server_side';
    let sid = sessionStorage.getItem('vp_session_id');
    if (!sid) {
        sid = 'sess_' + Math.random().toString(36).substring(2, 15) + '_' + Date.now();
        sessionStorage.setItem('vp_session_id', sid);
    }
    return sid;
};

// Call this on app start/login
export const startSession = async (user) => {
    if (!user) return;
    try {
        const uid = user.uid;
        const context = getGA4Context();
        const userRef = doc(db, 'users', uid);

        const updates = {
            lastLogin: serverTimestamp(),
            lastActivity: serverTimestamp(),
            country: context.user_country,
            city: context.city,
            device: context.device_type,
            browser: context.browser,
            operatingSystem: context.operating_system,
            language: context.language,
            lastLandingPage: context.page_url,
            totalSessions: increment(1)
        };

        // Capture initial visits or UTMs
        if (context.utm_source) updates.utmSource = context.utm_source;
        if (context.utm_medium) updates.utmMedium = context.utm_medium;
        if (context.utm_campaign) updates.utmCampaign = context.utm_campaign;
        if (context.utm_content) updates.utmContent = context.utm_content;
        if (context.utm_term) updates.utmTerm = context.utm_term;
        if (context.source && context.source !== 'direct') updates.trafficSource = context.source;

        await setDoc(userRef, updates, { merge: true });
    } catch (e) {
        console.error("Error in startSession:", e);
    }
};

// Call this on page navigation
export const trackPageView = async (path) => {
    // Handled automatically via GA4 and useActivityTracker hooks
};

// Call to record time spent
export const heartbeat = async () => {
    // Handled automatically via Google Analytics 4
};

// Call this to track custom funnel events via GA4 & Firestore Database
export const trackFunnelEvent = async (eventName, additionalData = {}) => {
    try {
        const currentUser = auth.currentUser;
        const uid = currentUser ? currentUser.uid : 'guest';
        const sessionId = getSessionId();

        // 1. Dispatch to GA4
        if (eventName === 'payment_success' || eventName === 'purchase') {
            const transactionId = additionalData.transaction_id || additionalData.transactionId;
            if (!transactionId) {
                console.error(`[Analytics error] Missing required transaction_id for ${eventName}. GA4 purchase event will NOT be fired.`);
            } else {
                const val = additionalData.value || (additionalData.product_name?.includes('Ebook') ? 199 : 1449);
                const currency = additionalData.currency || 'INR';
                const productName = additionalData.product_name || (val === 199 ? 'Versant Test Mastery Ebook' : 'VersantPro Premium Pass');

                const purchaseParams = {
                    transaction_id: transactionId,
                    value: Number(val),
                    currency: currency,
                    items: [
                        {
                            item_id: val === 199 ? 'ebook_mastery' : 'pro_pass_19',
                            item_name: productName,
                            price: Number(val),
                            quantity: 1
                        }
                    ]
                };
                
                // Dispatch standard GA4 purchase event
                trackGA4Event('purchase', purchaseParams);
            }
            
            // Also log the custom event for backward compatibility and funnel tracking
            if (eventName !== 'purchase') {
                trackGA4Event(eventName, additionalData);
            }
        } else {
            trackGA4Event(eventName, additionalData);
        }

        // 2. Fetch context variables
        const context = getGA4Context();

        // 3. Log event document to Firestore
        const eventDoc = {
            eventName,
            uid,
            sessionId,
            timestamp: serverTimestamp(),
            pageUrl: context.page_url,
            pageTitle: context.page_title,
            referrer: context.referrer,
            country: context.user_country,
            city: context.city,
            device: context.device_type,
            browser: context.browser,
            operatingSystem: context.operating_system,
            language: context.language,
            utmSource: context.utm_source,
            utmMedium: context.utm_medium,
            utmCampaign: context.utm_campaign,
            utmContent: context.utm_content,
            utmTerm: context.utm_term,
            additionalData
        };

        await addDoc(collection(db, 'analytics_events'), eventDoc);

        // 4. Update user aggregates in Firestore if user is authenticated
        if (currentUser) {
            const userRef = doc(db, 'users', uid);
            const userUpdates = {
                lastActivity: serverTimestamp(),
                country: context.user_country,
                city: context.city,
                device: context.device_type,
                browser: context.browser,
                lastLandingPage: context.page_url
            };

            // Set profile properties for specific lifecycle events
            if (eventName === 'user_registered') {
                userUpdates.registrationDate = serverTimestamp();
                userUpdates.firstVisitDate = serverTimestamp();
                userUpdates.firstLandingPage = context.page_url;
                userUpdates.totalSessions = 1;
                userUpdates.testsStarted = 0;
                userUpdates.testsCompleted = 0;
                userUpdates.totalRevenue = 0;
            } else if (eventName === 'user_login') {
                userUpdates.lastLogin = serverTimestamp();
                userUpdates.totalSessions = increment(1);
            } else if (eventName === 'test_started') {
                userUpdates.testsStarted = increment(1);
            } else if (eventName === 'test_completed') {
                userUpdates.testsCompleted = increment(1);
            } else if (eventName === 'score_generated') {
                if (additionalData.score !== undefined) {
                    userUpdates.lastScore = additionalData.score;
                }
            } else if (eventName === 'payment_success') {
                const value = additionalData.value || 0;
                userUpdates.totalRevenue = increment(value);
                userUpdates.lastPurchaseDate = serverTimestamp();
            } else if (eventName === 'ebook_downloaded') {
                userUpdates.ebookPurchased = true;
            }

            // Sync UTM attributes if captured
            if (context.utm_source) userUpdates.utmSource = context.utm_source;
            if (context.utm_medium) userUpdates.utmMedium = context.utm_medium;
            if (context.utm_campaign) userUpdates.utmCampaign = context.utm_campaign;
            if (context.utm_content) userUpdates.utmContent = context.utm_content;
            if (context.utm_term) userUpdates.utmTerm = context.utm_term;

            await setDoc(userRef, userUpdates, { merge: true });
        }
    } catch (e) {
        console.error("Error in trackFunnelEvent:", e);
    }
};
